import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { SelfieSpot } from '../components/WhoAmI';
import { MoveToBrowser } from '../components/MoveToBrowser';
import { PUT } from '../data/put-me-in-copy';
import { browserName } from '../data/keep-copy';
import { imaginePostId, loadProfile, recordPutMeIn, PUT_ME_IN_ID, IMAGINE_ID } from '../lib/state';
import { lookPutMeIn, sendPutMeIn, type PutMeInView } from '../lib/put-me-in';
import { markOpened } from '../lib/reveal';
import { inAppBrowser, inAppName, platform } from '../lib/in-app-browser';
import { useViewed } from '../lib/viewed';
import { track } from '../analytics';

/** How often the tray looks again while the crew draw. */
const POLL_MS = 4000;

type Step = 'loading' | 'ask' | 'drawing' | 'reveal';

/**
 * Put Yourself In It (owner, 2026-10-30; mocks on artifact BiYqKFwuiLEzycxDATLUR6).
 * Day 1's next stop on the road, after the imagine print and the email: the
 * photo that Imagine Yourself There used to ask for before the picture had even
 * developed, asked now over the picture itself, its plain stand-in keeping
 * their place.
 *
 *  ask      their print, "Now put yourself in it.", a selfie (or the library);
 *           with a photo in, "That's who goes in." and Draw me in. "Not now"
 *           goes back to the road, where the stop stays lit.
 *  drawing  the print in the tray. They may stay or go: it lands on the post,
 *           so the road and Today knock about it when it is dry.
 *  reveal   "There you are.", the plain one tucked behind. Keep this one ticks
 *           the stop and keeps the drawing as "me" (Who are you? then uses it);
 *           Try another photo while photos are left.
 *
 * When the picker opens and nothing comes back (Facebook's browser on
 * Android), a sheet offers the road in the phone's own browser.
 */
export function PutMeIn() {
  const nav = useNavigate();
  const [profile] = useState(loadProfile);
  const postId = imaginePostId(profile);
  const day = Number(Object.entries(profile.days).find(([, d]) => d.challengeId === IMAGINE_ID)?.[0]) || 1;
  const [step, setStep] = useState<Step>('loading');
  const [view, setView] = useState<PutMeInView | null>(null);
  const [selfie, setSelfie] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string>();
  const [help, setHelp] = useState(false);
  const sentAt = useRef(0);
  useViewed('put-me-in', step);

  /** Where the post stands decides the step: a drawing still out goes to the tray, a finished one to the reveal. */
  const land = useCallback((v: PutMeInView) => {
    setView(v);
    if (v.status === 'drawing') { setStep('drawing'); return; }
    if (v.status === 'drawn') {
      if (sentAt.current) track('put_me_in_result', { status: 'drawn', ms: Date.now() - sentAt.current });
      setStep('reveal'); return;
    }
    if (v.status === 'failed' && sentAt.current) { track('put_me_in_result', { status: 'failed', ms: Date.now() - sentAt.current }); setNote(v.left > 0 ? PUT.failed : PUT.noMore); setSelfie(undefined); }
    setStep('ask');
  }, []);

  useEffect(() => {
    if (!postId) return;
    lookPutMeIn(postId).then(land, () => { setView({ postId, status: 'ready', left: 1 }); setStep('ask'); });
  }, [postId, land]);

  // the tray looks again until the drawing lands
  useEffect(() => {
    if (step !== 'drawing' || !postId) return;
    const t = setInterval(() => { lookPutMeIn(postId).then(land, () => {}); }, POLL_MS);
    return () => clearInterval(t);
  }, [step, postId, land]);

  // the drawing is seen here, so it is not also waiting face-down on Today
  useEffect(() => { if (step === 'reveal' && postId && view?.after) void markOpened(postId, view.after); }, [step, postId, view?.after]);

  const onSilent = useCallback(() => { if (inAppBrowser()) { track('put_me_in_help_shown'); setHelp(true); } }, []);

  if (!postId) return <Navigate to="/path" replace />;

  const toRoad = (from: Step) => { track('put_me_in_later', { step: from }); nav('/path'); };
  const draw = async () => {
    if (!selfie) return;
    setSending(true); setNote(undefined);
    track('put_me_in_sent', { left: view?.left });
    try {
      sentAt.current = Date.now();
      land(await sendPutMeIn(postId, selfie));
    } catch (e) {
      const code = (e as { code?: string }).code;
      track('put_me_in_refused', { code: code || 'error' });
      setNote(code === 'crew_off' ? PUT.crewOff : code === 'too_many' ? PUT.noMore : code === 'busy' ? undefined : PUT.failed);
      if (code === 'busy') setStep('drawing');
    } finally { setSending(false); }
  };
  const keep = () => {
    if (!view?.after) return;
    track('put_me_in_kept', { left: view.left });
    recordPutMeIn(loadProfile(), postId, view.after);
    nav('/path', { viewTransition: true, state: { landed: PUT_ME_IN_ID } });
  };
  const again = () => { track('put_me_in_again', { left: view?.left }); setSelfie(undefined); setNote(undefined); setStep('ask'); };

  const tag = <span className="tag">Day {day} · {PUT.tag}</span>;

  if (step === 'loading') return <Frame theme="fit put-me-in" left={tag}><div className="put-print in-tray" aria-busy /></Frame>;

  if (step === 'drawing') {
    return (
      <Frame theme="fit put-me-in" left={tag}>
        <div className="put-print in-tray">
          <span className="tray-liquid" aria-hidden="true"><span className="ripple" /><span className="ripple r2" /></span>
          <img className="tray-peek" src="/art/reveal/moon-top.webp" alt="" draggable={false} />
          <p className="bubble tray-says" role="status">{PUT.drawing}<span className="dots3"><i /><i /><i /></span></p>
          {view?.before && <img className="put-art developing" src={view.before} alt="" draggable={false} />}
        </div>
        <h1>{PUT.drawingTitle}</h1>
        <p className="why" style={{ fontSize: 16 }}>{PUT.drawingBody}</p>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn" onClick={() => toRoad('drawing')}>{PUT.back}</button>
        </div>
      </Frame>
    );
  }

  if (step === 'reveal' && view?.after) {
    return (
      <Frame theme="fit put-me-in" left={tag}>
        <div className="put-reveal">
          {view.before && <img className="put-behind" src={view.before} alt="The picture before" draggable={false} />}
          <img className="put-art put-front" src={view.after} alt="You, in your picture" draggable={false} />
        </div>
        <h1>{PUT.revealTitle}</h1>
        <div className="stack" style={{ marginTop: 'auto' }}>
          <button className="btn coral" onClick={keep}>{PUT.keep}</button>
          {view.left > 0 && <div className="row" style={{ justifyContent: 'center' }}><button className="link" onClick={again}>{PUT.again}</button></div>}
        </div>
      </Frame>
    );
  }

  const os = platform();
  const kind = inAppBrowser();
  const outOfPhotos = view?.left === 0;
  return (
    <Frame theme="fit put-me-in" left={tag}>
      <h1>{selfie ? PUT.chosenTitle : PUT.title}</h1>
      <div className="put-ask">
        {view?.before && <img className="put-art" src={view.before} alt="Your picture, with a plain figure keeping your place" draggable={false} />}
        {!outOfPhotos && <SelfieSpot selfie={selfie} camera source="put-me-in" label={PUT.photo} onBusy={setBusy} onSilent={onSilent} onSelfie={(s) => { setSelfie(s); if (s) setHelp(false); }} />}
      </div>
      <p className="why" style={{ fontSize: 16 }}>{selfie ? PUT.chosenBody : PUT.body}</p>
      {note && <p className="hint put-note" role="status">{note}</p>}
      {!selfie && <p className="hint" style={{ textAlign: 'center' }}>{PUT.privacy}</p>}
      <div className="stack" style={{ marginTop: 'auto' }}>
        {selfie && <button className="btn coral" disabled={busy || sending} onClick={draw}>{PUT.draw}</button>}
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="link" onClick={() => toRoad('ask')}>{PUT.later}</button>
        </div>
      </div>
      {help && kind && (
        <div className="put-help" role="dialog" aria-label={PUT.helpTitle}>
          <div className="sheet">
            <b style={{ fontSize: 18 }}>{PUT.helpTitle}</b>
            <p className="why" style={{ margin: 0 }}>{PUT.helpBody(inAppName(kind), browserName(os))}</p>
            <MoveToBrowser from="put-me-in" />
            <button className="link" onClick={() => { track('put_me_in_help_closed'); setHelp(false); }}>{PUT.helpClose}</button>
          </div>
        </div>
      )}
    </Frame>
  );
}
